// V17.262: pure formatting for the AR Residential daily team board.
//
// Kept out of the components so the wall display and the page cannot drift,
// and so the rules are testable without rendering anything.

const DASH = '—';

/** Poll cadence is 30s; three missed polls means the board is lying. */
export const STALE_THRESHOLD_MS = 90_000;

/** LAK amounts are large; a wall display reads "33.0M" far better than digits. */
export function formatLak(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  return `${(value / 1_000_000).toFixed(1)}M`;
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  return value.toLocaleString();
}

/** Percentages are never clamped — beating target is information worth showing. */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  return `${Math.round(value)}%`;
}

export type Tone = 'good' | 'warn' | 'bad' | 'none';

export function progressTone(value: number | null | undefined): Tone {
  if (value === null || value === undefined) return 'none';
  if (value >= 90) return 'good';
  if (value >= 50) return 'warn';
  return 'bad';
}

/** The number is unclamped; the bar is not — a 166% bar would overflow its track. */
export function barWidth(value: number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  return Math.max(0, Math.min(100, value));
}

/**
 * True when the payload is old enough that presenting it as live would mislead.
 * An unparseable timestamp counts as stale: better a visible warning than a
 * confident wrong number.
 */
export function isStale(
  generatedAt: string,
  now: Date,
  thresholdMs: number = STALE_THRESHOLD_MS,
): boolean {
  const generated = new Date(generatedAt).getTime();
  if (Number.isNaN(generated)) return true;
  return now.getTime() - generated > thresholdMs;
}
